import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  RotateCcw,
  Sparkles,
  ArrowRight,
  MapPin,
  Compass,
  Lock,
  CheckCircle2,
  AlertCircle,
  FileJson,
  FolderPlus,
  RefreshCw,
  Route,
  Upload,
} from 'lucide-react';
import { FolderDropzone } from '@/components/ingestion/FolderDropzone';
import { IngestionProgress } from '@/components/ingestion/IngestionProgress';
import { IngestionSummary } from '@/components/ingestion/IngestionSummary';
import { IngestedFileRecord } from '@/types/domain';
import {
  getManagedTrips,
  attachPolarstepsTrace,
  ManagedTripOption,
} from '@/services/tripManager';

export interface AdminIngestViewProps {
  pipeline: any;
  stagedPhotos: IngestedFileRecord[];
  setStagedPhotos: (photos: IngestedFileRecord[]) => void;
  stagedPolarsteps: { locationsFile?: File; tripFile?: File };
  setStagedPolarsteps: (files: { locationsFile?: File; tripFile?: File }) => void;
  onOpenIngestedMap: (tripId?: string | number) => void;
  onResetTrip: () => void;
  onBackToCatalog: () => void;
  hasStoredData: boolean;
  storedPhotoCount: number;
  onTripUpdated?: () => void;
}

export const AdminIngestView: React.FC<AdminIngestViewProps> = ({
  pipeline,
  stagedPhotos,
  setStagedPhotos,
  stagedPolarsteps,
  setStagedPolarsteps,
  onOpenIngestedMap,
  onResetTrip,
  onBackToCatalog,
  hasStoredData,
  storedPhotoCount,
  onTripUpdated,
}) => {
  // Available trips in the catalog / database
  const [tripOptions, setTripOptions] = useState<ManagedTripOption[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<string | number>('islanda-trip');
  const [selectedTripName, setSelectedTripName] = useState<string>('La nostra Islanda');
  const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
  const [newTripTitle, setNewTripTitle] = useState<string>('');

  // Ingestion Mode: append to existing trip photos vs replace
  const [ingestMode, setIngestMode] = useState<'append' | 'replace'>('append');

  // Dedicated Polarsteps Trace upload state
  const [traceFile, setTraceFile] = useState<File | null>(null);
  const [isAttachingTrace, setIsAttachingTrace] = useState<boolean>(false);
  const [traceFeedback, setTraceFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
    locationCount?: number;
    photosReGeotagged?: number;
  } | null>(null);

  const directTraceInputRef = useRef<HTMLInputElement>(null);

  // Load available trips
  const loadTrips = useCallback(async () => {
    const trips = await getManagedTrips();
    setTripOptions(trips);

    // Keep current selection if valid, or default to Iceland
    if (trips.length > 0) {
      const current = trips.find((t) => t.id === selectedTripId);
      if (current) {
        setSelectedTripName(current.name);
      } else {
        const iceland = trips.find(
          (t) => t.id === 'islanda-trip' || t.name.toLowerCase().includes('islanda')
        );
        if (iceland) {
          setSelectedTripId(iceland.id);
          setSelectedTripName(iceland.name);
        } else {
          setSelectedTripId(trips[0].id);
          setSelectedTripName(trips[0].name);
        }
      }
    }
  }, [selectedTripId]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  // Selected trip information summary
  const currentTripInfo = tripOptions.find((t) => t.id === selectedTripId);
  const activeTripDisplayName = isCreatingNew
    ? (newTripTitle.trim() || 'Nuovo Viaggio')
    : selectedTripName;

  // Handle changing target trip
  const handleSelectTrip = (id: string | number) => {
    if (id === '__NEW__') {
      setIsCreatingNew(true);
      setSelectedTripId('new-trip-' + Date.now());
      setSelectedTripName(newTripTitle.trim() || 'Nuovo Viaggio');
    } else {
      setIsCreatingNew(false);
      setSelectedTripId(id);
      const found = tripOptions.find((t) => t.id === id);
      if (found) {
        setSelectedTripName(found.name);
      }
    }
    setTraceFeedback(null);
  };

  // Direct Polarsteps Trace attachment handler
  const handleAttachTrace = async () => {
    const fileToUse = traceFile || stagedPolarsteps.locationsFile || stagedPolarsteps.tripFile;
    if (!fileToUse) return;

    setIsAttachingTrace(true);
    setTraceFeedback(null);

    const targetId = isCreatingNew
      ? `trip-${Date.now()}`
      : selectedTripId;
    const targetName = isCreatingNew
      ? (newTripTitle.trim() || 'Nuovo Viaggio')
      : selectedTripName;

    try {
      const res = await attachPolarstepsTrace(targetId, targetName, {
        locationsFile: fileToUse,
        tripFile: fileToUse,
      });

      if (res.success) {
        setTraceFeedback({
          type: 'success',
          message: res.message,
          locationCount: res.locationCount,
          photosReGeotagged: res.photosReGeotagged,
        });
        setTraceFile(null);
        setStagedPolarsteps({});
        await loadTrips();
        if (onTripUpdated) {
          onTripUpdated();
        }
      } else {
        setTraceFeedback({
          type: 'error',
          message: res.message,
        });
      }
    } catch (err: unknown) {
      setTraceFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsAttachingTrace(false);
    }
  };

  // Start photo processing attached to selected trip
  const handleStartIngestion = () => {
    const targetId = isCreatingNew
      ? `trip-${Date.now()}`
      : selectedTripId;
    const targetName = isCreatingNew
      ? (newTripTitle.trim() || 'Nuovo Viaggio')
      : selectedTripName;

    pipeline.startIngestion(stagedPhotos, stagedPolarsteps, {
      targetTripId: targetId,
      targetTripName: targetName,
      mode: ingestMode,
    });
  };

  return (
    <div className="flex-1 overflow-y-auto bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* 1. Admin Sub-header Banner */}
      <div className="border-b border-slate-800/80 bg-slate-900/60 backdrop-blur-md px-4 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBackToCatalog}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition cursor-pointer"
              title="Torna al Catalogo Viaggi"
              aria-label="Torna al Catalogo"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-amber-500/20 text-amber-400 flex items-center justify-center border border-amber-500/30">
                  <Lock className="w-3 h-3" />
                </div>
                <h2 className="text-sm sm:text-base font-bold text-white">
                  Pannello Amministratore & Gestione Viaggi
                </h2>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Scegli il viaggio di destinazione, carica nuove foto o aggiungi la traccia Polarsteps.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-center">
            {hasStoredData && (
              <button
                type="button"
                onClick={onResetTrip}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-950/40 hover:bg-rose-900/60 text-rose-200 hover:text-white rounded-xl text-xs font-semibold border border-rose-700/60 transition shadow-sm cursor-pointer"
                title="Azzera il database locale e riparti da zero"
              >
                <RotateCcw className="w-3.5 h-3.5 text-rose-400" />
                <span>Azzera Database</span>
              </button>
            )}

            <button
              type="button"
              onClick={onBackToCatalog}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-sky-600/20 hover:bg-sky-600/30 text-sky-300 hover:text-white rounded-xl text-xs font-semibold border border-sky-500/40 transition cursor-pointer"
            >
              <span>Vedi Catalogo Pubblico</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* 2. Main Admin Content Body */}
      <div className="flex-1 p-4 sm:p-6 lg:p-8 flex flex-col items-center max-w-4xl mx-auto w-full">
        {/* State 1: Staging & Management */}
        {(pipeline.status === 'idle' || pipeline.status === 'staging') && (
          <div className="w-full flex flex-col gap-6">
            {/* Target Trip Selector Card */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800/80">
                <div>
                  <span className="text-xs font-bold text-sky-400 uppercase tracking-wider">
                    Destinazione Caricamento
                  </span>
                  <h3 className="text-base sm:text-lg font-bold text-white mt-0.5">
                    A quale viaggio vuoi aggiungere dati?
                  </h3>
                </div>

                {/* Dropdown Trip Selector */}
                <div className="flex items-center gap-2">
                  <select
                    value={isCreatingNew ? '__NEW__' : selectedTripId}
                    onChange={(e) => handleSelectTrip(e.target.value)}
                    className="bg-slate-800 border border-slate-700 text-slate-100 rounded-xl px-3.5 py-2 text-sm font-semibold focus:outline-none focus:border-sky-500 transition cursor-pointer"
                  >
                    {tripOptions.map((trip) => (
                      <option key={trip.id} value={trip.id}>
                        {trip.name} {trip.photoCount > 0 ? `(${trip.photoCount} foto)` : ''}
                      </option>
                    ))}
                    <option value="__NEW__">+ Crea Nuovo Viaggio...</option>
                  </select>
                </div>
              </div>

              {/* If creating new trip, text input */}
              {isCreatingNew && (
                <div className="mt-4 pt-4 border-t border-slate-800/60 flex flex-col sm:flex-row items-center gap-3">
                  <span className="text-xs font-semibold text-slate-300 shrink-0">
                    Nome Nuovo Viaggio:
                  </span>
                  <input
                    type="text"
                    value={newTripTitle}
                    onChange={(e) => {
                      setNewTripTitle(e.target.value);
                      setSelectedTripName(e.target.value.trim() || 'Nuovo Viaggio');
                    }}
                    placeholder="Es. Norvegia 2025, Tour Andalusia..."
                    className="flex-1 bg-slate-950 border border-slate-700 text-slate-100 rounded-xl px-3.5 py-2 text-sm font-medium focus:outline-none focus:border-sky-500 transition"
                  />
                </div>
              )}

              {/* Selected Trip Status Summary */}
              {!isCreatingNew && currentTripInfo && (
                <div className="mt-4 pt-3 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Viaggio selezionato:</span>
                    <span className="font-bold text-white">{currentTripInfo.name}</span>
                    <span className="px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-300 font-semibold border border-slate-700 text-xs">
                      {currentTripInfo.photoCount} foto salvate
                    </span>
                  </div>

                  {/* Polarsteps Status Badge */}
                  <div>
                    {currentTripInfo.hasPolarsteps ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-950/60 text-emerald-300 border border-emerald-800/60 font-semibold text-xs">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        <span>Traccia GPS Presente ({currentTripInfo.locationCount} punti, {currentTripInfo.stepCount} tappe)</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-950/60 text-amber-300 border border-amber-800/60 font-semibold text-xs">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                        <span>Traccia Polarsteps Mancante</span>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action 1: Add Polarsteps Trace Specifically */}
            <div className="bg-slate-900/90 border border-sky-900/40 rounded-2xl p-5 shadow-xl flex flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-sky-500/15 text-sky-400 border border-sky-500/30">
                  <Route className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-white">
                    Aggiungi / Aggiorna Traccia Polarsteps per &quot;{activeTripDisplayName}&quot;
                  </h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Carica il file <code className="text-sky-300">locations.json</code> o <code className="text-sky-300">trip.json</code> esportato da Polarsteps. Le foto già caricate verranno ri-geolocalizzate e collegate al percorso!
                  </p>
                </div>
              </div>

              {/* Hidden file input */}
              <input
                type="file"
                ref={directTraceInputRef}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setTraceFile(e.target.files[0]);
                    setTraceFeedback(null);
                  }
                }}
                accept=".json,application/json"
                className="hidden"
              />

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <button
                  type="button"
                  disabled={isAttachingTrace}
                  onClick={() => directTraceInputRef.current?.click()}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-xl text-xs sm:text-sm font-semibold transition cursor-pointer"
                >
                  <FileJson className="w-4 h-4 text-sky-400" />
                  <span>
                    {traceFile
                      ? `File selezionato: ${traceFile.name}`
                      : 'Seleziona File locations.json / trip.json'}
                  </span>
                </button>

                {(traceFile || stagedPolarsteps.locationsFile || stagedPolarsteps.tripFile) && (
                  <button
                    type="button"
                    disabled={isAttachingTrace}
                    onClick={handleAttachTrace}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-500 hover:to-blue-500 text-white rounded-xl text-xs sm:text-sm font-bold shadow-lg shadow-sky-500/20 transition cursor-pointer active:scale-95"
                  >
                    {isAttachingTrace ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Salvataggio e ricollegamento foto in corso...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        <span>Salva Traccia su &quot;{activeTripDisplayName}&quot; e Collega Foto</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Trace Success/Error Feedback */}
              {traceFeedback && (
                <div
                  className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm ${
                    traceFeedback.type === 'success'
                      ? 'bg-emerald-950/60 border-emerald-800 text-emerald-200'
                      : 'bg-red-950/60 border-red-800 text-red-200'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    {traceFeedback.type === 'success' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                    )}
                    <span>{traceFeedback.message}</span>
                  </div>

                  {traceFeedback.type === 'success' && (
                    <button
                      type="button"
                      onClick={() => onOpenIngestedMap(selectedTripId)}
                      className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs transition cursor-pointer shadow-sm self-start sm:self-center"
                    >
                      Esplora sulla Mappa &rarr;
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Action 2: Add Photo Folders to Selected Trip */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-xl flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-purple-500/15 text-purple-400 border border-purple-500/30">
                    <FolderPlus className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white">
                      Carica Cartelle Foto per &quot;{activeTripDisplayName}&quot;
                    </h4>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Trascina cartelle di foto con categorie (es. <em>Panorami</em>, <em>Con me</em>).
                    </p>
                  </div>
                </div>

                {/* Append vs Replace Mode Option */}
                <div className="flex items-center gap-4 bg-slate-950/80 px-3 py-1.5 rounded-xl border border-slate-800 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer text-slate-300 hover:text-white">
                    <input
                      type="radio"
                      name="ingestMode"
                      checked={ingestMode === 'append'}
                      onChange={() => setIngestMode('append')}
                      className="text-sky-500 focus:ring-0"
                    />
                    <span>Aggiungi alle esistenti</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-slate-400 hover:text-white">
                    <input
                      type="radio"
                      name="ingestMode"
                      checked={ingestMode === 'replace'}
                      onChange={() => setIngestMode('replace')}
                      className="text-sky-500 focus:ring-0"
                    />
                    <span>Sostituisci foto</span>
                  </label>
                </div>
              </div>

              {/* Folder Dropzone */}
              <FolderDropzone
                onPhotosIngested={(p) => setStagedPhotos(p)}
                onPolarstepsFiles={(files) => setStagedPolarsteps(files)}
              />

              {/* Launch Ingestion Button */}
              {stagedPhotos.length > 0 && (
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={handleStartIngestion}
                    className="flex items-center gap-2.5 px-8 py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl text-sm font-bold transition shadow-lg shadow-blue-500/25 animate-bounce cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>
                      Avvia Elaborazione ({stagedPhotos.length} foto) per &quot;{activeTripDisplayName}&quot;
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Bottom Actions */}
            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              {hasStoredData && (
                <button
                  type="button"
                  onClick={() => onOpenIngestedMap(selectedTripId)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-sky-600/20 hover:bg-sky-600/30 text-sky-300 hover:text-white border border-sky-500/40 rounded-xl text-xs font-semibold backdrop-blur-md shadow-md transition cursor-pointer"
                >
                  <MapPin className="w-4 h-4 text-sky-400" />
                  <span>Esplora Mappa Database ({storedPhotoCount} foto)</span>
                </button>
              )}

              <button
                type="button"
                onClick={onBackToCatalog}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 rounded-xl text-xs font-semibold transition cursor-pointer"
              >
                <Compass className="w-4 h-4 text-slate-400" />
                <span>Torna al Catalogo</span>
              </button>
            </div>
          </div>
        )}

        {/* State 2: Active Processing */}
        {(pipeline.status === 'processing' ||
          pipeline.status === 'paused' ||
          pipeline.status === 'parsing_polarsteps') && (
          <IngestionProgress
            stats={pipeline.progressStats}
            isPaused={pipeline.isPaused}
            onPause={pipeline.pause}
            onResume={pipeline.resume}
            onCancel={pipeline.cancel}
          />
        )}

        {/* State 3: Completion Summary */}
        {pipeline.status === 'completed' && pipeline.completionSummary && (
          <div className="w-full flex flex-col items-center gap-4">
            <IngestionSummary
              summary={pipeline.completionSummary}
              onOpenMap={() => onOpenIngestedMap(selectedTripId)}
              onReset={() => {
                pipeline.reset();
                setStagedPhotos([]);
                loadTrips();
                if (onTripUpdated) onTripUpdated();
              }}
            />

            <button
              type="button"
              onClick={onBackToCatalog}
              className="mt-3 flex items-center gap-2 px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-xl text-xs font-semibold border border-slate-700 transition cursor-pointer"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>Torna al Catalogo Viaggi Pubblico</span>
            </button>
          </div>
        )}

        {/* State 4: Error State */}
        {pipeline.status === 'error' && (
          <div className="p-5 bg-red-950/60 border border-red-800 rounded-2xl text-red-200 text-sm max-w-md text-center shadow-xl">
            <p className="font-semibold mb-1">Si è verificato un errore durante l'elaborazione</p>
            <p className="text-xs text-red-300 mb-4">{pipeline.errorMessage}</p>
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => pipeline.reset()}
                className="px-4 py-1.5 bg-red-900/60 hover:bg-red-800/80 rounded-lg text-xs font-medium transition cursor-pointer"
              >
                Riprova
              </button>
              <button
                type="button"
                onClick={onBackToCatalog}
                className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium transition cursor-pointer"
              >
                Torna al Catalogo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
